import {
  Button,
  Checkbox,
  CountrySelect,
  DatePicker,
  Input,
  NumberInput,
  PhoneInput,
  RadioGroup,
  RegularModal,
  Select,
} from '@/components_deprecated'
import {
  Field as BaseField,
  Form as BaseForm,
} from '@/modules/users/user-settings/components'
import type {
  DieselCardForm,
  EditVehicleDetailsForm,
  EditVehicleDetailsModalResolve,
  VehicleDetailInput,
} from '@/modules/vehicles/types'
import { VehicleDetailFormField } from '@/modules/vehicles/types'
import type { VehicleDetailNode } from '@/modules/vehicles/types.graphql'
import type { CountryCode } from '@/utils/countries'
import type { PromptResolve } from '@ur-solutions/react-components'
import { Icon } from '@ur-solutions/react-components'
import type { FormValidatorFn } from '@ur-solutions/react-hooks'
import { useForm, useTranslate } from '@ur-solutions/react-hooks'
import React from 'react'
import styled from 'styled-components'
import theme from '../../../../theme'

const Form = styled(BaseForm)`
  flex-direction: row;
  flex-wrap: wrap;
  & > div {
    width: 100%;
  }
  & > div {
    width: 100%;
  }
  .--checkbox-field {
    width: 50%;
  }
`

const Field = styled(BaseField)<{ hidden?: boolean }>`
  display: ${props => (props.hidden ? 'none' : 'flex')};
`

const Container = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
  margin-bottom: 16px;
`

const CustomInput = styled(Input)`
  width: 100%;
`

const CustomButton = styled(Button)<{ deleteButton?: boolean }>`
  padding: 1rem 1.2rem;
  color: white;
  min-width: 60px;
  margin-top: ${props => (props.deleteButton ? '28px' : '0')};
`

const ContainerFields = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;

  & > label {
    display: block;
    font-size: 0.9rem;
    font-weight: 600;
    margin: 0 0 6px 6px;
    color: ${props => props.theme.colors.gray5};
  }
`

interface EditVehicleDetailsModalProps {
  title: string
  vehicle: VehicleDetailNode
  fields: VehicleDetailInput[]
  onSubmit: PromptResolve<EditVehicleDetailsModalResolve | null>
  onDelete?: (id: string) => Promise<void>
}

interface Accumulator {
  [key: string]: any
  registrationDate: Date | null
  department: string | null
}

function resolveFieldValue(
  field: VehicleDetailInput,
  vehicle: VehicleDetailNode
) {
  const value = vehicle[field.key]

  if (field.key === 'dieselCardNumber') {
    return vehicle?.dieselCards
  }

  if (!field.type) return value
  if (field.type === 'date') {
    return value && new Date(value as string)
  }
  return value
}

export const EditVehicleDetailsModal: React.FC<
  EditVehicleDetailsModalProps
> = ({ title, vehicle, fields, onSubmit, onDelete }) => {
  const translations = useTranslate({
    edit: 'vehicles.edit-vehicle',
    date: 'common.date',
    pinCode: 'vehicles.pin-code',
    validation: {
      required: 'common.required',
      onlyNumbers: 'common.only-number',
    },
  })

  const initialValues: EditVehicleDetailsForm = fields.reduce((acc, field) => {
    let value = resolveFieldValue(field, vehicle)
    if (field.key === 'dieselCardNumber') {
      value =
        Array.isArray(value) && value.length > 0
          ? value.map(num => ({
              id: num?.id || null,
              number: num?.number || '',
              pinCode: num?.pinCode || '',
            }))
          : [{ id: null, number: '', pinCode: '' }]
    }
    acc[field.key] = value
    return acc
  }, {} as Accumulator)

  const {
    formValues: form,
    formChangeHandler: handler,
    submitHandler: submit,
    updateForm,
    formErrors: errors,
    formValid,
  } = useForm<EditVehicleDetailsForm>({
    values: initialValues,
    validators: {
      dieselCardNumber: ((value: unknown) => {
        const cards = value as DieselCardForm[] | undefined
        if (!cards || !Array.isArray(cards)) return null

        const validationErrors: Record<number, Partial<DieselCardForm>> = {}

        cards.forEach((card, index) => {
          const cardError: Partial<DieselCardForm> = {}
          if (!card.number) cardError.number = translations.validation.required
          if (!card.pinCode) {
            cardError.pinCode = translations.validation.required
          } else if (!/^\d*$/.test(card.pinCode)) {
            cardError.pinCode = translations.validation.onlyNumbers
          }
          if (Object.keys(cardError).length) validationErrors[index] = cardError
        })

        return Object.keys(validationErrors).length ? validationErrors : null
      }) as FormValidatorFn<unknown, EditVehicleDetailsForm>,
    },
  })

  const formFields = (() => {
    return fields.map(field => {
      const fieldValue = form[field.key]
      const requireDependencies = field.dependencies?.requireFields
      const hidden =
        requireDependencies &&
        fields?.filter(
          evalField =>
            requireDependencies?.includes(evalField.key as string) &&
            !!form[evalField.key]
        ).length < requireDependencies.length

      const defaultReturn = () => {
        return (
          <Field key={field.key} hidden={hidden}>
            <label>{field.label}</label>
            <Input
              value={(form[field.key] as string) ?? ''}
              disabled={field.readOnly}
              onChange={handler(field.key)}
            />
          </Field>
        )
      }

      const handleClick = async (
        value: DieselCardForm,
        index: number,
        dieselCards: DieselCardForm[]
      ) => {
        try {
          const newValues = dieselCards.filter((_, i) => i !== index)
          if (value?.id && onDelete) {
            void onDelete(value.id)
          }
          updateForm({ dieselCardNumber: newValues })
        } catch (error) {
          console.error('Failed to delete diesel card:', error)
        }
      }

      if (field?.key === 'dieselCardNumber') {
        const dieselCards = form?.dieselCardNumber as DieselCardForm[]

        const errorDieselCards = (errors?.dieselCardNumber ?? {}) as Record<
          number,
          Partial<DieselCardForm>
        >

        return (
          <Field key={field.key} hidden={hidden}>
            {dieselCards?.length > 0 ? (
              dieselCards?.map((value, index) => (
                <Container
                  key={index}
                  style={{
                    display: 'flex',
                    gap: '8px',
                  }}
                >
                  <ContainerFields key={index}>
                    <label>{field.label}</label>
                    <CustomInput
                      value={value?.number}
                      onChange={(newValue: string) => {
                        const newValues = [
                          ...dieselCards,
                        ] as Partial<DieselCardForm>[]
                        newValues[index] = {
                          ...newValues[index],
                          number: newValue,
                        }
                        updateForm({
                          dieselCardNumber: newValues as DieselCardForm[],
                        })
                      }}
                    />
                    {errorDieselCards?.[index]?.number && (
                      <div
                        style={{ color: theme.colors.red, fontSize: '12px' }}
                      >
                        {errorDieselCards[index].number}
                      </div>
                    )}
                    <label>{translations.pinCode}</label>
                    <CustomInput
                      value={value?.pinCode}
                      onChange={(newValue: string) => {
                        const newValues = [
                          ...dieselCards,
                        ] as Partial<DieselCardForm>[]
                        newValues[index] = {
                          ...newValues[index],
                          pinCode: newValue,
                        }
                        updateForm({
                          dieselCardNumber: newValues as DieselCardForm[],
                        })
                      }}
                    />
                    {errorDieselCards?.[index]?.pinCode && (
                      <div
                        style={{ color: theme.colors.red, fontSize: '12px' }}
                      >
                        {errorDieselCards[index].pinCode}
                      </div>
                    )}
                  </ContainerFields>

                  <CustomButton
                    type="button"
                    deleteButton
                    onClick={async () =>
                      await handleClick(value, index, dieselCards)
                    }
                  >
                    <Icon icon="trash" cursor="pointer" color="black" />
                  </CustomButton>
                </Container>
              ))
            ) : (
              <label>{field.label}</label>
            )}
            <CustomButton
              type="button"
              onClick={() =>
                updateForm({
                  dieselCardNumber: [
                    ...dieselCards,
                    { id: null, number: '', pinCode: '' },
                  ],
                })
              }
            >
              <Icon icon="plus" cursor="pointer" color="white" />
            </CustomButton>
          </Field>
        )
      }

      switch (field.type) {
        case VehicleDetailFormField.Date:
          return (
            <Field key={field.key} hidden={hidden}>
              <label>{field.label}</label>
              <DatePicker
                fullWidth
                displayDateFormat={'PP'}
                value={(fieldValue as Date | null) ?? new Date()}
                clearable
                disabled={field.readOnly}
                initCleared={fieldValue === null}
                placeholder={translations.date}
                onChange={(value, cleared) =>
                  handler(field.key)(cleared ? null : value)
                }
              />
            </Field>
          )
        case VehicleDetailFormField.Boolean:
          return (
            <Field
              className="--checkbox-field"
              alignLeft
              key={field.key}
              hidden={hidden}
            >
              <Checkbox
                checked={form[field.key] as boolean}
                label={field.label}
                disabled={field.readOnly}
                onChange={handler(field.key)}
              />
            </Field>
          )
        case VehicleDetailFormField.Number:
          return (
            <Field key={field.key} hidden={hidden}>
              <label>
                {field.label} {field.unit && `(${field.unit})`}
              </label>
              <NumberInput
                value={(form[field.key] as number) ?? ''}
                disabled={field.readOnly}
                onChange={handler(field.key)}
              />
            </Field>
          )
        case VehicleDetailFormField.Country:
          return (
            <Field key={field.key} hidden={hidden}>
              <label>{field.label}</label>
              <CountrySelect
                value={form[field.key] as CountryCode}
                fullWidth
                onChange={handler(field.key)}
              />
            </Field>
          )
        case VehicleDetailFormField.Select:
          if (field.options && !(form[field.key] instanceof Date)) {
            return (
              <Field key={field.key} hidden={hidden}>
                <label>{field.label}</label>
                <Select
                  value={form[field.key] as string | number | null}
                  options={field.options}
                  fullWidth
                  onChange={handler(field.key)}
                />
              </Field>
            )
          } else {
            return defaultReturn()
          }
        case VehicleDetailFormField.Radio:
          if (field.options && !(form[field.key] instanceof Date)) {
            return (
              <Field key={field.key} hidden={hidden}>
                <label>{field.label}</label>
                <RadioGroup
                  value={(form[field.key] ?? '') as string | number}
                  radios={field.options}
                  onChange={handler(field.key)}
                />
              </Field>
            )
          } else {
            return defaultReturn()
          }
        case VehicleDetailFormField.Phone:
          return (
            <Field key={field.key} hidden={hidden}>
              <label>{field.label}</label>
              <PhoneInput
                value={form[field.key] as string}
                height="54px"
                background="inherit"
                onChange={handler(field.key)}
              />
            </Field>
          )
        default:
          return defaultReturn()
      }
    })
  })()

  return (
    <RegularModal
      title={`${translations.edit}: ${title}`}
      cancelOnClickOutside
      overflowY="auto"
      width="480px"
      onCancel={() => onSubmit(null)}
      onSubmit={submit(form => onSubmit({ form }))}
      submitDisabled={!formValid}
    >
      <Form>{formFields}</Form>
    </RegularModal>
  )
}
